import React, { useState, useEffect } from 'react';
import apiClient from '../api/apiClient';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../utils/roles';
import './ClassManagementPage.css';

const ClassManagementPage = () => {
  const { user } = useAuth();
  
  // Admin flow: Vælg sprogcenter først
  const [selectedScId, setSelectedScId] = useState(null);
  const [selectedScName, setSelectedScName] = useState('');

  useEffect(() => {
    // Hvis SC_ADMIN, sæt ID automatisk
    if (user && user.role === ROLES.SC_ADMIN) {
      setSelectedScId(user.scId);
    }
  }, [user]);

  if (user?.role === ROLES.ADMIN && !selectedScId) {
    return <SchoolSelectionView onSelect={(id, navn) => {
      setSelectedScId(id);
      setSelectedScName(navn);
    }} />;
  }

  return (
    <TerminInputView 
      scId={selectedScId} 
      scName={selectedScName} 
      onBack={user?.role === ROLES.ADMIN ? () => setSelectedScId(null) : null} 
    />
  );
};

// ------------------------------------------------------
// SUB-COMPONENT 1: Vælg Sprogcenter (Uændret fra sidst)
// ------------------------------------------------------
const SchoolSelectionView = ({ onSelect }) => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/api/sprogcentre')
      .then(res => { setCenters(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div>
      <h1 className="page-title">Indberetning af Hold</h1>
      <p style={{marginBottom: '20px'}}>Vælg sprogcenter for at indtaste prøvedeltagere.</p>
      {loading ? <p>Henter...</p> : (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px'}}>
          {centers.map(sc => (
            <div key={sc.id} onClick={() => onSelect(sc.id, sc.navn)} className="exam-card" style={{cursor: 'pointer', textAlign:'left'}}>
              <h3 style={{color: '#003366', marginBottom: '5px'}}>{sc.navn}</h3>
              <p style={{color: '#666', fontSize:'0.9rem'}}>Klik for at åbne</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------
// SUB-COMPONENT 2: Termin & Deltager Input (NYT DESIGN)
// ------------------------------------------------------
const TerminInputView = ({ scId, scName, onBack }) => {
  const [terminer, setTerminer] = useState([]);
  const [selectedTerminId, setSelectedTerminId] = useState('');
  const [holds, setHolds] = useState([]); // De 4 rækker (PD1, PD2...)
  const [loading, setLoading] = useState(false);
  
  // Er terminen låst? (Vi tjekker om bare én af rækkerne er låst)
  const isLocked = holds.length > 0 && holds[0].låst;

  // 1. Hent terminer ved start
  useEffect(() => {
    apiClient.get('/api/terminer').then(res => {
      const allTermins = res.data;
      setTerminer(allTermins);
      
      // Beregn Default Termin (Dags dato)
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth(); // 0 = Januar ... 11 = December
      
      // Hvis vi er i Juli (6) eller senere, så hører vi til "Vinter" terminen (indberetning efterår/vinter)
      // Hvis vi er før Juli, så hører vi til "Sommer" terminen (indberetning forår/sommer)
      
      const isSecondHalf = currentMonth >= 6; // Juli - Dec
      
      // Tilpas søgeordene til dine data ('Vinter', 'Sommer', 'Forår')
      // Her siger vi: Hvis 2. halvår -> Vinter. Hvis 1. halvår -> Sommer (eller Forår)
      const seasonSearch = isSecondHalf ? 'Vinter' : 'Sommer'; 
      
      let defaultTerm = allTermins.find(t => 
        t.navn.includes(currentYear.toString()) && 
        (t.navn.includes(seasonSearch) || (seasonSearch === 'Sommer' && t.navn.includes('Forår')))
      );

      // Fallback: Hvis vi ikke finder den, tag den nyeste
      if (!defaultTerm && allTermins.length > 0) defaultTerm = allTermins[0];

      if (defaultTerm) setSelectedTerminId(defaultTerm.id);
    });
  }, []);

  // 2. Hent data når termin (eller skole) ændres
  useEffect(() => {
    if (selectedTerminId && scId) {
      loadCounts();
    }
  }, [selectedTerminId, scId]);

  const loadCounts = async () => {
    setLoading(true);
    try {
      // Henter (og opretter hvis mangler) de 4 rækker
      const res = await apiClient.get(`/api/hold/summary?scId=${scId}&termin_id=${selectedTerminId}`);
      setHolds(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Håndter input ændring
  const handleInputChange = (id, newVal) => {
    // Opdater lokalt state
    setHolds(prev => prev.map(h => h.id === id ? { ...h, deltagere: parseInt(newVal) || 0 } : h));
  };

  // Gem ændringer (løbende eller ved blur - her gemmer vi når man trykker "Indberet" eller forlader?)
  // Kravet var: "Skal kunne ændres frit". Vi gemmer automatisk ved 'blur' (når man klikker væk) for god UX.
  const handleBlur = async () => {
    if (isLocked) return;
    try {
      // Forbered opdateringer
      const updates = holds.map(h => ({ id: h.id, deltagere: h.deltagere }));
      await apiClient.put('/api/hold/update-counts', { 
        updates,
        termin_id: selectedTerminId,
        sprogcenter_id: scId
      });
      
      console.log('Gemt kladder...');
    } catch (err) { console.error('Fejl ved auto-save'); }
  };

  // Lås Termin
  const handleLockTermin = async () => {
    if (!window.confirm('Er du sikker på, at du vil låse terminen og sende deltagerantal til SIRI?\nHoldene bliver automatisk oprettet.')) return;
    
    try {
      await apiClient.post('/api/hold/lock-termin', { 
        termin_id: selectedTerminId, 
        sprogcenter_id: scId 
      });
      loadCounts(); 
      alert('Terminen er nu indberettet, og holdene er genereret i databasen.');
    } catch (err) { alert('Fejl ved indberetning.'); }
  };

  return (
    <div>
      {onBack && (
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px'}}>
          <button 
            onClick={onBack} 
            style={{
              padding: '8px 16px', 
              cursor: 'pointer',
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '6px',
              color: '#555',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            &larr; Vælg andet sprogcenter
          </button>
          
          {/* Vis kun navnet her for Admin, da de skifter mellem skoler */}
          {scName && <span style={{color: '#666', fontWeight: '600'}}>{scName}</span>}
        </div>
      )}

      {/* Termin Vælger */}
      <div className="termin-selector">
        <div>
          <label className="termin-label">Valgt Termin:</label>
          <select 
            className="termin-select"
            value={selectedTerminId}
            onChange={(e) => setSelectedTerminId(e.target.value)}
          >
            {terminer.map(t => (
              <option key={t.id} value={t.id}>{t.navn}</option>
            ))}
          </select>
        </div>
        <div>
          {/* Status indikator */}
          {isLocked ? (
            <span style={{color: 'red', fontWeight: 'bold'}}>🔒 LÅST (Indberettet)</span>
          ) : (
            <span style={{color: 'green', fontWeight: 'bold'}}>🔓 ÅBEN (Kladde)</span>
          )}
        </div>
      </div>

      {loading ? <p>Indlæser data...</p> : (
        <div className="table-card">
          <h2 style={{textAlign: 'center', marginBottom: '2rem', color: '#333'}}>
            Indtast antal prøvedeltagere
          </h2>
          
          <div className="exams-grid">
            {holds.map(h => (
              <div key={h.id} className="exam-card">
                <label className="exam-title">{h.prøve}</label>
                <input 
                  type="number" 
                  className="exam-input"
                  value={h.deltagere}
                  disabled={isLocked} // Lås input hvis terminen er låst
                  onChange={(e) => handleInputChange(h.id, e.target.value)}
                  onBlur={handleBlur} // Gem når man forlader feltet
                  min="0"
                />
                <p style={{marginTop: '10px', color: '#888', fontSize: '0.8rem'}}>deltagere</p>
              </div>
            ))}
          </div>

          {/* Indberet Knap */}
          <div className="lock-section">
            {isLocked ? (
              <div className="status-locked">
                Denne termin er indberettet og låst. Kontakt SIRI ved fejl.
              </div>
            ) : (
              <div>
                <p style={{marginBottom: '15px', color: '#555'}}>
                  Når du har indtastet alle tallene korrekt, skal du trykke herunder for at indsende til SIRI.
                </p>
                <button className="btn-lock" onClick={handleLockTermin}>
                  Indberet & Lås Termin
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagementPage;